'use client';
import React, { useState } from 'react';
import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';
import { StockCount, CountLineItem } from './StockCountContent';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (count: StockCount) => void;
}

const SITES = ['Site Alpha', 'Site Beta', 'Site Gamma', 'Warehouse B'];

const AVAILABLE_ITEMS = [
  { id: 'item-001', itemCode: 'STL-REBAR-12', itemName: 'TMT Steel Rebar 12mm', category: 'RAW_MATERIAL', uom: 'MT', systemQty: 18, unitCost: 680 },
  { id: 'item-002', itemCode: 'CHEM-CEM-OPC', itemName: 'OPC Cement 53 Grade', category: 'RAW_MATERIAL', uom: 'BAG', systemQty: 820, unitCost: 7.20 },
  { id: 'item-003', itemCode: 'SAF-HARN-01', itemName: 'Full Body Safety Harness', category: 'PPE', uom: 'EA', systemQty: 3, unitCost: 142 },
  { id: 'item-004', itemCode: 'PPE-HELM-WHT', itemName: 'Safety Helmet White (ISI)', category: 'PPE', uom: 'EA', systemQty: 18, unitCost: 12.50 },
  { id: 'item-005', itemCode: 'SAF-FEXT-CO2', itemName: 'Fire Extinguisher CO₂ 5kg', category: 'SAFETY_ITEM', uom: 'EA', systemQty: 12, unitCost: 89.50 },
  { id: 'item-006', itemCode: 'TOOL-GRND-4IN', itemName: 'Angle Grinder 4" 850W', category: 'TOOL', uom: 'EA', systemQty: 7, unitCost: 68 },
  { id: 'item-007', itemCode: 'LUB-HYD-46', itemName: 'Hydraulic Oil ISO 46', category: 'CONSUMABLE', uom: 'L', systemQty: 12, unitCost: 4.80 },
  { id: 'item-008', itemCode: 'MECH-BRNG-6205', itemName: 'Deep Groove Ball Bearing 6205', category: 'SPARE_PART', uom: 'EA', systemQty: 4, unitCost: 18.40 },
  { id: 'item-009', itemCode: 'ELE-CBL-25', itemName: 'Electrical Cable 2.5mm²', category: 'RAW_MATERIAL', uom: 'M', systemQty: 480, unitCost: 1.35 },
  { id: 'item-010', itemCode: 'PPE-GLOVE-L', itemName: 'Nitrile Gloves (Large)', category: 'PPE', uom: 'PR', systemQty: 24, unitCost: 0.95 },
  { id: 'item-011', itemCode: 'SEAL-ORING-32', itemName: 'O-Ring NBR 32mm ID', category: 'SPARE_PART', uom: 'EA', systemQty: 148, unitCost: 0.45 },
  { id: 'item-012', itemCode: 'WELD-ROD-308', itemName: 'Welding Electrode SS 308', category: 'CONSUMABLE', uom: 'KG', systemQty: 32, unitCost: 14.20 },
];

interface LineEntry {
  itemId: string;
  physicalQty: string;
  notes: string;
}

export default function StockCountEntryModal({ open, onClose, onSubmit }: Props) {
  const [site, setSite] = useState('');
  const [countDate, setCountDate] = useState(new Date().toISOString().split('T')[0]);
  const [lines, setLines] = useState<LineEntry[]>([{ itemId: '', physicalQty: '', notes: '' }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fieldClass = (hasError: boolean) =>
    `w-full px-3 py-2 border rounded-lg text-sm bg-input text-foreground placeholder:text-muted-foreground/60 outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-all ${hasError ? 'border-red-400' : 'border-border'}`;

  const addLine = () => setLines((prev) => [...prev, { itemId: '', physicalQty: '', notes: '' }]);
  const removeLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const updateLine = (idx: number, field: keyof LineEntry, value: string) => {
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!site) errs.site = 'Site is required';
    if (!countDate) errs.countDate = 'Count date is required';
    lines.forEach((l, i) => {
      if (!l.itemId) errs[`item-${i}`] = 'Select an item';
      if (l.physicalQty === '' || isNaN(Number(l.physicalQty)) || Number(l.physicalQty) < 0) {
        errs[`qty-${i}`] = 'Enter a valid quantity';
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    await new Promise((r) => setTimeout(r, 1000));

    const lineItems: CountLineItem[] = lines.map((l, i) => {
      const item = AVAILABLE_ITEMS.find((it) => it.id === l.itemId)!;
      const physQty = Number(l.physicalQty);
      const variance = physQty - item.systemQty;
      const variancePct = item.systemQty > 0 ? (variance / item.systemQty) * 100 : 0;
      const varianceValue = variance * item.unitCost;
      return {
        id: `cli-new-${i}`,
        itemCode: item.itemCode,
        itemName: item.itemName,
        category: item.category,
        uom: item.uom,
        systemQty: item.systemQty,
        physicalQty: physQty,
        variance,
        variancePct: Math.round(variancePct * 100) / 100,
        unitCost: item.unitCost,
        varianceValue: Math.round(varianceValue * 100) / 100,
        notes: l.notes,
      };
    });

    const variantItems = lineItems.filter((li) => li.variance !== 0).length;
    const countNum = `CNT-2026-${String(Math.floor(Math.random() * 900) + 100).padStart(4, '0')}`;

    const newCount: StockCount = {
      id: `cnt-new-${Date.now()}`,
      countNumber: countNum,
      site,
      countDate: new Date(countDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      createdBy: 'Rahul Kumar',
      status: 'PENDING_APPROVAL',
      lineItems,
      totalItems: lineItems.length,
      variantItems,
      adjustmentPosted: false,
    };

    setIsSubmitting(false);
    onSubmit(newCount);
    // Reset
    setSite('');
    setCountDate(new Date().toISOString().split('T')[0]);
    setLines([{ itemId: '', physicalQty: '', notes: '' }]);
    setErrors({});
  };

  const usedItemIds = lines.map((l) => l.itemId).filter(Boolean);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Physical Stock Count"
      subtitle="Enter physical quantities per site — variances are calculated automatically against system balances"
      size="xl"
      footer={
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Submitting will send for manager approval. Adjustments are posted only after approval.
          </p>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg text-sm font-500 hover:bg-muted transition-colors">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-5 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-600 hover:bg-amber-500 transition-all active:scale-95 disabled:opacity-60"
            >
              {isSubmitting ? (
                <><Icon name="ArrowPathIcon" size={15} className="animate-spin" /> Submitting…</>
              ) : (
                <><Icon name="PaperAirplaneIcon" size={15} /> Submit for Approval</>
              )}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Header fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-600 text-foreground mb-1.5">Site <span className="text-red-500">*</span></label>
            <select
              value={site}
              onChange={(e) => setSite(e.target.value)}
              className={fieldClass(!!errors.site)}
            >
              <option value="">Select site…</option>
              {SITES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {errors.site && <p className="text-red-500 text-[11px] mt-1">{errors.site}</p>}
          </div>
          <div>
            <label className="block text-xs font-600 text-foreground mb-1.5">Count Date <span className="text-red-500">*</span></label>
            <input
              type="date"
              value={countDate}
              onChange={(e) => setCountDate(e.target.value)}
              className={fieldClass(!!errors.countDate)}
            />
            {errors.countDate && <p className="text-red-500 text-[11px] mt-1">{errors.countDate}</p>}
          </div>
        </div>

        {/* Line items */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-600 text-foreground">Physical Count Entries</h3>
            <button
              onClick={addLine}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-secondary/30 text-secondary rounded-lg text-xs font-600 hover:bg-secondary/5 transition-colors"
            >
              <Icon name="PlusIcon" size={13} />
              Add Item
            </button>
          </div>

          {/* Column headers */}
          <div className="grid grid-cols-12 gap-2 px-2 mb-1">
            <div className="col-span-4 text-[10px] font-600 uppercase text-muted-foreground tracking-wide">Item</div>
            <div className="col-span-2 text-[10px] font-600 uppercase text-muted-foreground tracking-wide">System Qty</div>
            <div className="col-span-2 text-[10px] font-600 uppercase text-muted-foreground tracking-wide">Physical Qty</div>
            <div className="col-span-2 text-[10px] font-600 uppercase text-muted-foreground tracking-wide">Variance</div>
            <div className="col-span-1 text-[10px] font-600 uppercase text-muted-foreground tracking-wide">Notes</div>
            <div className="col-span-1"></div>
          </div>

          <div className="space-y-2">
            {lines.map((line, idx) => {
              const item = AVAILABLE_ITEMS.find((it) => it.id === line.itemId);
              const physQty = line.physicalQty !== '' ? Number(line.physicalQty) : null;
              const variance = item && physQty !== null ? physQty - item.systemQty : null;
              const variancePct = item && variance !== null && item.systemQty > 0
                ? ((variance / item.systemQty) * 100).toFixed(1)
                : null;

              return (
                <div key={`line-${idx}`} className="grid grid-cols-12 gap-2 items-start bg-muted/30 rounded-lg p-2 border border-border/50">
                  {/* Item select */}
                  <div className="col-span-4">
                    <select
                      value={line.itemId}
                      onChange={(e) => updateLine(idx, 'itemId', e.target.value)}
                      className={`${fieldClass(!!errors[`item-${idx}`])} text-xs`}
                    >
                      <option value="">Select item…</option>
                      {AVAILABLE_ITEMS.filter((it) => !usedItemIds.includes(it.id) || it.id === line.itemId).map((it) => (
                        <option key={it.id} value={it.id}>{it.itemCode} — {it.itemName}</option>
                      ))}
                    </select>
                    {errors[`item-${idx}`] && <p className="text-red-500 text-[10px] mt-0.5">{errors[`item-${idx}`]}</p>}
                  </div>

                  {/* System qty */}
                  <div className="col-span-2 flex items-center h-9">
                    <div className="px-3 py-2 bg-muted border border-border rounded-lg text-xs text-muted-foreground w-full text-center">
                      {item ? `${item.systemQty} ${item.uom}` : '—'}
                    </div>
                  </div>

                  {/* Physical qty */}
                  <div className="col-span-2">
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      value={line.physicalQty}
                      onChange={(e) => updateLine(idx, 'physicalQty', e.target.value)}
                      placeholder="0"
                      className={`${fieldClass(!!errors[`qty-${idx}`])} text-xs text-center`}
                    />
                    {errors[`qty-${idx}`] && <p className="text-red-500 text-[10px] mt-0.5">{errors[`qty-${idx}`]}</p>}
                  </div>

                  {/* Variance display */}
                  <div className="col-span-2 flex items-center h-9">
                    {variance !== null ? (
                      <div className={`px-2 py-1.5 rounded-lg text-xs font-600 w-full text-center border ${
                        variance === 0
                          ? 'bg-teal-50 text-teal-700 border-teal-200'
                          : variance < 0
                          ? 'bg-red-50 text-red-700 border-red-200' :'bg-amber-50 text-amber-700 border-amber-200'
                      }`}>
                        {variance > 0 ? '+' : ''}{variance} ({variancePct}%)
                      </div>
                    ) : (
                      <div className="px-2 py-1.5 bg-muted border border-border rounded-lg text-xs text-muted-foreground w-full text-center">—</div>
                    )}
                  </div>

                  {/* Notes */}
                  <div className="col-span-1">
                    <input
                      type="text"
                      value={line.notes}
                      onChange={(e) => updateLine(idx, 'notes', e.target.value)}
                      placeholder="Note…"
                      className={`${fieldClass(false)} text-xs`}
                    />
                  </div>

                  {/* Remove */}
                  <div className="col-span-1 flex items-center justify-center h-9">
                    {lines.length > 1 && (
                      <button
                        onClick={() => removeLine(idx)}
                        className="p-1.5 rounded-md hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors"
                      >
                        <Icon name="TrashIcon" size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Variance summary */}
        {lines.some((l) => l.itemId && l.physicalQty !== '') && (
          <div className="bg-muted/50 border border-border rounded-xl p-4">
            <h4 className="text-xs font-600 text-foreground mb-3 flex items-center gap-2">
              <Icon name="CalculatorIcon" size={14} className="text-secondary" />
              Variance Summary
            </h4>
            <div className="grid grid-cols-3 gap-4 text-center">
              {(() => {
                const filled = lines.filter((l) => l.itemId && l.physicalQty !== '');
                const withVariance = filled.filter((l) => {
                  const item = AVAILABLE_ITEMS.find((it) => it.id === l.itemId);
                  return item && Number(l.physicalQty) !== item.systemQty;
                });
                const totalVarValue = filled.reduce((acc, l) => {
                  const item = AVAILABLE_ITEMS.find((it) => it.id === l.itemId);
                  if (!item) return acc;
                  return acc + (Number(l.physicalQty) - item.systemQty) * item.unitCost;
                }, 0);
                return (
                  <>
                    <div>
                      <p className="text-lg font-700 text-foreground">{filled.length}</p>
                      <p className="text-[10px] text-muted-foreground">Items Counted</p>
                    </div>
                    <div>
                      <p className={`text-lg font-700 ${withVariance.length > 0 ? 'text-red-600' : 'text-teal-600'}`}>{withVariance.length}</p>
                      <p className="text-[10px] text-muted-foreground">With Variance</p>
                    </div>
                    <div>
                      <p className={`text-lg font-700 ${totalVarValue < 0 ? 'text-red-600' : totalVarValue > 0 ? 'text-amber-600' : 'text-teal-600'}`}>
                        {totalVarValue >= 0 ? '+' : ''}€{totalVarValue.toFixed(2)}
                      </p>
                      <p className="text-[10px] text-muted-foreground">Net Variance Value</p>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
